import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { AuthContext } from "../context/AuthContext";
import api from "../config/api";
import "./Profile.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.tinubu-ambode.org";

const DEFAULT_PROFILE_SVG =
  "<svg xmlns='http://www.w3.org/2000/svg' width='120' height='150' viewBox='0 0 120 150'><rect width='120' height='150' rx='14' fill='%23eef2f7'/><circle cx='60' cy='50' r='22' fill='%23cbd5e1'/><path d='M20 136c7-22 23-34 40-34s33 12 40 34' fill='%23cbd5e1'/><circle cx='60' cy='50' r='11' fill='%2394a3b8'/></svg>";

const DEFAULT_PROFILE_IMAGE =
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(DEFAULT_PROFILE_SVG)}`;

const PrintableIDCard = forwardRef(({ userData, profileImage }, ref) => {
  const fullName =
    userData?.full_name ||
    `${userData?.first_name || ""} ${userData?.surname || ""}`.trim();

  const joinedDate = userData?.created_at
    ? new Date(userData.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  return (
    <div ref={ref} className="print-id-container">
      <div className="id-card">
        <div className="id-card-topbar">
          <div className="id-card-mark">TAS</div>
          <div className="id-card-heading">
            <p>Tinubu Ambode Supporters</p>
            <h3>Member Identification Card</h3>
          </div>
        </div>

        <div className="id-card-body">
          <div className="id-card-photo-wrap">
            <img
              src={profileImage}
              alt="Member"
              onError={(e) => {
                e.currentTarget.src = DEFAULT_PROFILE_IMAGE;
              }}
            />
            <span
              className={`id-card-badge ${userData?.status?.toLowerCase() || "active"}`}
            >
              {userData?.status || "Active"}
            </span>
          </div>

          <div className="id-card-details">
            <div className="id-card-name-block">
              <h2>{fullName || "N/A"}</h2>
              <span className="id-card-role">
                {userData?.membership_category || "Volunteer"}
              </span>
            </div>

            <div className="id-card-info-grid">
              <div className="id-card-info-item">
                <span className="id-card-label">Member ID</span>
                <span className="id-card-value">
                  {userData?.membership_id || "N/A"}
                </span>
              </div>

              {userData?.geographic_info?.ward ? (
                <div className="id-card-info-item">
                  <span className="id-card-label">Ward</span>
                  <span className="id-card-value">
                    {userData.geographic_info.ward}
                  </span>
                </div>
              ) : null}

              <div className="id-card-info-item">
                <span className="id-card-label">Joined</span>
                <span className="id-card-value">{joinedDate}</span>
              </div>

              <div className="id-card-info-item">
                <span className="id-card-label">Phone</span>
                <span className="id-card-value">
                  {userData?.telephone_numbers?.[0] ||
                    userData?.whatsapp_number ||
                    "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="id-card-footer">
          <div className="id-footer-left">
            <span>Verified Member</span>
            <strong>{new Date().getFullYear()}</strong>
          </div>
          <div className="id-footer-right">
            <span>Card No.</span>
            <strong>{userData?.membership_id || "N/A"}</strong>
          </div>
        </div>
      </div>
    </div>
  );
});

PrintableIDCard.displayName = "PrintableIDCard";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutSuccess, setShowLogoutSuccess] = useState(false);
  const [profileImage, setProfileImage] = useState("");
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const navigate = useNavigate();
  const { updateUser } = useContext(AuthContext);
  const idCardRef = useRef(null);

  const getAddressFromUser = useCallback((user, fallbackAddress = "") => {
    if (!user || typeof user !== "object") return fallbackAddress || "";

    const directAddress =
      typeof user.full_house_address === "string"
        ? user.full_house_address.trim()
        : "";

    if (directAddress) return directAddress;

    const nestedAddress = [
      user.address?.full_house_address,
      user.address?.full_address,
      user.address?.address,
      user.profile?.full_house_address,
      user.profile?.address,
      user.contact?.address,
      user.residential_address,
    ].find((value) => typeof value === "string" && value.trim() !== "");

    if (nestedAddress) return nestedAddress;

    return fallbackAddress || "";
  }, []);

  const buildFormDataFromUser = useCallback(
    (user, fallbackAddress = "") => ({
      first_name: user?.first_name || "",
      surname: user?.surname || "",
      email: user?.email || "",
      telephone_numbers: user?.telephone_numbers || [""],
      whatsapp_number: user?.whatsapp_number || "",
      date_of_birth: user?.date_of_birth || "",
      gender: user?.gender || "",
      full_house_address: getAddressFromUser(user, fallbackAddress),
      state_code: user?.state_code || "",
      sd_code: user?.sd_code || "",
      fc_code: user?.fc_code || "",
      lcda_code: user?.lcda_code || "",
      lg_code: user?.lg_code || "",
      ward_code: user?.ward_code || "",
      pu_code: user?.pu_code || "",
      membership_category: user?.membership_category || "",
    }),
    [getAddressFromUser]
  );

  const handleDownloadIDCardPdf = async () => {
    if (!idCardRef.current) {
      showToast("Unable to download ID card right now", "error");
      return;
    }

    try {
      const cardElement =
        idCardRef.current.querySelector(".id-card") || idCardRef.current;

      const fetchImageAsDataUrl = async (src, requestHeaders = {}) => {
        if (!src) return "";

        const response = await fetch(src, {
          headers: requestHeaders,
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();

        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error("Failed to read image blob"));
          reader.readAsDataURL(blob);
        });
      };

      const resolveProfileImageForExport = async (src) => {
        if (!src || src === DEFAULT_PROFILE_IMAGE) {
          return src;
        }

        if (src.startsWith("data:") || src.startsWith("blob:")) {
          return src;
        }

        const normalizedSrc = src.startsWith("http")
          ? src
          : `${API_BASE_URL}${src.startsWith("/") ? "" : "/"}${src}`;

        const proxySrc = `https://images.weserv.nl/?url=${encodeURIComponent(
          normalizedSrc
        )}`;

        try {
          const token = localStorage.getItem("jwt_token");

          return await fetchImageAsDataUrl(
            normalizedSrc,
            token ? { Authorization: `Bearer ${token}` } : {}
          );
        } catch {
          try {
            return await fetchImageAsDataUrl(proxySrc);
          } catch {
            return proxySrc;
          }
        }
      };

      const imageElements = Array.from(cardElement.querySelectorAll("img"));
      const exportImageSrc = await resolveProfileImageForExport(profileImage);

      if (exportImageSrc && exportImageSrc !== DEFAULT_PROFILE_IMAGE) {
        await new Promise((resolve) => {
          const exportImage = new Image();
          exportImage.crossOrigin = "anonymous";
          exportImage.onload = () => resolve();
          exportImage.onerror = () => resolve();
          exportImage.src = exportImageSrc;
        });
      }

      await Promise.all(
        imageElements.map(
          (image) =>
            new Promise((resolve) => {
              if (image.complete && image.naturalWidth > 0) {
                resolve();
                return;
              }

              image.onload = () => resolve();
              image.onerror = () => resolve();
            })
        )
      );

      const canvas = await html2canvas(cardElement, {
        scale: 3,
        useCORS: true,
        allowTaint: false,
        imageTimeout: 15000,
        backgroundColor: "#ffffff",
        onclone: (clonedDocument) => {
          const clonedImage = clonedDocument.querySelector(
            ".id-card-photo-wrap img"
          );

          if (clonedImage && exportImageSrc) {
            clonedImage.setAttribute("src", exportImageSrc);
            clonedImage.setAttribute("crossorigin", "anonymous");
          }
        },
      });

      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [85.6, 54],
      });

      const pageWidth = 85.6;
      const pageHeight = 54;
      const imageAspectRatio = canvas.width / canvas.height;
      const pageAspectRatio = pageWidth / pageHeight;
      let imageWidth = pageWidth;
      let imageHeight = pageHeight;

      if (imageAspectRatio > pageAspectRatio) {
        imageHeight = pageWidth / imageAspectRatio;
      } else {
        imageWidth = pageHeight * imageAspectRatio;
      }

      const imageX = (pageWidth - imageWidth) / 2;
      const imageY = (pageHeight - imageHeight) / 2;

      pdf.addImage(imageData, "PNG", imageX, imageY, imageWidth, imageHeight);
      const fileName = `${userData?.membership_id || "member-id-card"}.pdf`;
      pdf.save(fileName);
      showToast("ID card downloaded successfully", "success");
    } catch (error) {
      console.error("[Profile] Failed to download ID card PDF", error);
      showToast("Unable to download ID card. Please try again.", "error");
    }
  };

  const interpretErrorMessage = (errorMessage) => {
    const errorMap = {
      "The full house address field must be a string":
        "Please enter a valid residential address",
      "The full house address field is required":
        "Residential address is required",
      "full_house_address must be a string":
        "Please enter a valid residential address",
      "The first name field is required": "First name is required",
      "The surname field is required": "Last name is required",
      "The first name field must be a string":
        "Please enter a valid first name",
      "The surname field must be a string":
        "Please enter a valid last name",
      "The email field is required": "Email address is required",
      "The email field must be a valid email address":
        "Please enter a valid email address",
      "The email has already been taken":
        "This email address is already registered",
      "The telephone numbers field is required": "Phone number is required",
      "The whatsapp number field is required":
        "WhatsApp number is required",
      "The telephone numbers must be a valid phone number":
        "Please enter a valid phone number",
      "The whatsapp number must be a valid phone number":
        "Please enter a valid WhatsApp number",
      "The date of birth field is required": "Date of birth is required",
      "The date of birth field must be a valid date":
        "Please enter a valid date of birth",
      "The gender field is required": "Please select your gender",
      "The current password field is required":
        "Current password is required to change your password",
      "The password field is required": "New password is required",
      "The password field must be at least 6 characters":
        "Password must be at least 6 characters long",
      "The password confirmation field is required":
        "Please confirm your new password",
      "The password confirmation does not match":
        "New password and confirmation do not match",
      "Current password is incorrect":
        "The current password you entered is incorrect",
      "The passport photo must be an image":
        "Please select a valid image file",
      "The passport photo must be a file of type: jpeg, png, jpg, gif":
        "Please select a JPEG, PNG, or GIF image",
      "The passport photo may not be greater than 2048 kilobytes":
        "Profile picture must be less than 2MB",
      "Failed to update profile":
        "Unable to save changes. Please try again.",
      Unauthenticated: "Your session has expired. Please login again.",
      "Network Error":
        "Unable to connect to server. Please check your internet connection.",
    };

    if (errorMap[errorMessage]) {
      return errorMap[errorMessage];
    }

    for (const [key, value] of Object.entries(errorMap)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return value;
      }
    }

    if (errorMessage.includes("must be a string")) {
      return "Please enter valid text in all required fields";
    }
    if (errorMessage.includes("is required")) {
      return "Please fill in all required fields";
    }
    if (errorMessage.includes("already been taken")) {
      return "This information is already registered with another account";
    }
    if (errorMessage.includes("valid") && errorMessage.includes("email")) {
      return "Please enter a valid email address";
    }
    if (errorMessage.includes("valid") && errorMessage.includes("phone")) {
      return "Please enter a valid phone number";
    }

    return "Please check your information and try again";
  };

  const toReadableFieldName = (field) => {
    const fieldMap = {
      full_house_address: "Residential address",
      first_name: "First name",
      surname: "Last name",
      email: "Email",
      telephone_numbers: "Phone number",
      whatsapp_number: "WhatsApp number",
      date_of_birth: "Date of birth",
      gender: "Gender",
      password: "Password",
      password_confirmation: "Password confirmation",
      current_password: "Current password",
      passport_photo: "Profile picture",
    };

    return (
      fieldMap[field] ||
      field
        .replace(/\[\d+\]/g, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    );
  };

  const formatBackendValidationErrors = (errors) => {
    if (!errors || typeof errors !== "object") return "";

    const formatted = Object.entries(errors)
      .flatMap(([field, messages]) => {
        const fieldLabel = toReadableFieldName(field);
        const list = Array.isArray(messages) ? messages : [messages];

        return list
          .filter(Boolean)
          .map((message) => `${fieldLabel}: ${interpretErrorMessage(String(message))}`);
      })
      .filter(Boolean);

    return formatted.join("\n");
  };

  const showToast = (message, type = "error", useRawMessage = false) => {
    const userFriendlyMessage =
      type === "error" && !useRawMessage
        ? interpretErrorMessage(message)
        : message;
    setToast({ show: true, message: userFriendlyMessage, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 5000);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem("authToken");
      const storedUserData = localStorage.getItem("userData");

      if (!token) {
        navigate("/login");
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await api.get("/auth/profile");

        if (response.data.status === "success" && response.data.data?.user) {
          const user = response.data.data.user;
          const cachedAddress = (() => {
            try {
              const parsed = storedUserData ? JSON.parse(storedUserData) : null;
              return parsed?.full_house_address || "";
            } catch {
              return "";
            }
          })();

          setUserData(user);
          setFormData(buildFormDataFromUser(user, cachedAddress));

          localStorage.setItem(
            "userData",
            JSON.stringify({
              ...user,
              full_house_address: getAddressFromUser(user, cachedAddress),
            })
          );

          if (user.profile_photo) {
            setProfileImage(user.profile_photo);
          } else if (user.files?.passport_photo) {
            setProfileImage(user.files.passport_photo);
          } else {
            setProfileImage(DEFAULT_PROFILE_IMAGE);
          }

          setError("");
        } else {
          throw new Error("Invalid response format from server");
        }
      } catch (error) {
        if (storedUserData) {
          try {
            const user = JSON.parse(storedUserData);
            setUserData(user);
            setFormData(buildFormDataFromUser(user, user?.full_house_address));

            if (user.profile_photo) {
              setProfileImage(user.profile_photo);
            } else if (user.files?.passport_photo) {
              setProfileImage(user.files.passport_photo);
            }

            setError("Using cached data - unable to connect to server");
          } catch (parseError) {
            setError("Failed to load profile data. Please login again.");
          }
        } else {
          setError("Failed to load profile data. Please login again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate, buildFormDataFromUser, getAddressFromUser]);

  const handleEditToggle = () => {
    if (isEditing) {
      handleSaveProfile();
    } else {
      setIsEditing(true);
      setPasswordData({
        current_password: "",
        password: "",
        password_confirmation: "",
      });
      setPasswordError("");
      setSelectedPhoto(null);
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
      return;
    }

    setSaveLoading(true);
    setPasswordError("");

    try {
      const normalizeToString = (value) => {
        if (value === null || value === undefined) return "";
        if (typeof value === "string") return value;
        if (typeof value === "number" || typeof value === "boolean") {
          return String(value);
        }

        if (Array.isArray(value)) {
          return value
            .map((item) =>
              item === null || item === undefined ? "" : String(item)
            )
            .filter((item) => item.trim() !== "")
            .join(", ");
        }

        if (typeof value === "object") {
          const preferredKeys = [
            "full_address",
            "address",
            "line1",
            "street",
            "city",
            "state",
          ];

          const pickedValues = preferredKeys
            .map((key) => value[key])
            .filter((item) => typeof item === "string" && item.trim() !== "");

          if (pickedValues.length > 0) {
            return pickedValues.join(", ");
          }

          const firstStringValue = Object.values(value).find(
            (item) => typeof item === "string" && item.trim() !== ""
          );
          if (firstStringValue) return firstStringValue;

          return "";
        }

        return "";
      };

      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        if (key === "telephone_numbers" && Array.isArray(formData[key])) {
          formData[key].forEach((number, index) => {
            formDataToSend.append(
              `telephone_numbers[${index}]`,
              normalizeToString(number)
            );
          });
        } else {
          formDataToSend.append(key, normalizeToString(formData[key]));
        }
      });

      if (passwordData.password) {
        if (passwordData.password !== passwordData.password_confirmation) {
          setPasswordError("New password and confirmation do not match");
          setSaveLoading(false);
          return;
        }
        if (passwordData.password.length < 6) {
          setPasswordError("Password must be at least 6 characters long");
          setSaveLoading(false);
          return;
        }

        formDataToSend.append("current_password", passwordData.current_password);
        formDataToSend.append("password", passwordData.password);
        formDataToSend.append(
          "password_confirmation",
          passwordData.password_confirmation
        );
      }

      if (selectedPhoto) {
        console.log("[Profile] Attempting profile photo upload", {
          name: selectedPhoto.name,
          type: selectedPhoto.type,
          size: selectedPhoto.size,
        });
        formDataToSend.append("passport_photo", selectedPhoto);
      }

      const response = await api.post("/user/profile", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        const profileResponse = await api.get("/auth/profile");

        if (
          profileResponse.data.status === "success" &&
          profileResponse.data.data?.user
        ) {
          const updatedUser = profileResponse.data.data.user;
          const addressFromForm =
            typeof formData.full_house_address === "string"
              ? formData.full_house_address.trim()
              : "";
          const normalizedAddress = getAddressFromUser(updatedUser, addressFromForm);

          const updatedUserWithAddress = {
            ...updatedUser,
            full_house_address: normalizedAddress,
          };

          setUserData(updatedUserWithAddress);
          setFormData(buildFormDataFromUser(updatedUserWithAddress, normalizedAddress));
          localStorage.setItem("userData", JSON.stringify(updatedUserWithAddress));

          if (updateUser) {
            updateUser(updatedUserWithAddress);
          }

          if (updatedUserWithAddress.profile_photo) {
            setProfileImage(updatedUserWithAddress.profile_photo);
          } else if (updatedUserWithAddress.files?.passport_photo) {
            setProfileImage(updatedUserWithAddress.files.passport_photo);
          }
        }

        setIsEditing(false);

        setPasswordData({
          current_password: "",
          password: "",
          password_confirmation: "",
        });
        setSelectedPhoto(null);

        setError("");
        showToast("Profile updated successfully!", "success");
      } else {
        throw new Error(response.data.message || "Failed to update profile");
      }
    } catch (error) {
      let errorMessage = "Failed to update profile";
      let rawValidationMessage = "";

      console.error("[Profile] Save failed", {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
        method: error?.config?.method,
        responseData: error?.response?.data,
      });

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.message) {
          errorMessage = errorData.message;
        }

        if (errorData.errors) {
          rawValidationMessage = formatBackendValidationErrors(errorData.errors);
          const detailedErrors = Object.values(errorData.errors).flat();
          errorMessage = detailedErrors.join(", ") || errorMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (rawValidationMessage) {
        showToast(rawValidationMessage, "error", true);
      } else {
        showToast(errorMessage, "error");
      }

      const userFriendlyError = interpretErrorMessage(errorMessage);
      if (userFriendlyError.toLowerCase().includes("password")) {
        setPasswordError(userFriendlyError);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (passwordError) {
      setPasswordError("");
    }
  };

  const handlePhoneNumberChange = (index, value) => {
    const updatedNumbers = [...(formData.telephone_numbers || [])];
    updatedNumbers[index] = value;
    setFormData((prev) => ({
      ...prev,
      telephone_numbers: updatedNumbers,
    }));
  };

  const createImageBitmapFromFile = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Failed to load image"));
      };

      img.src = objectUrl;
    });

  const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), type, quality);
    });

  const compressImageToLimit = async (file, maxSizeBytes) => {
    const image = await createImageBitmapFromFile(file);

    const maxDimension = 1280;
    const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
    const targetWidth = Math.max(1, Math.round(image.width * scale));
    const targetHeight = Math.max(1, Math.round(image.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to get canvas context");
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const outputType = file.type === "image/png" ? "image/jpeg" : file.type;
    const extension = outputType === "image/jpeg" ? "jpg" : "png";
    const baseName = file.name.replace(/\.[^/.]+$/, "");

    let quality = 0.9;
    let blob = await canvasToBlob(canvas, outputType, quality);

    while (blob && blob.size > maxSizeBytes && quality > 0.4) {
      quality -= 0.1;
      blob = await canvasToBlob(canvas, outputType, quality);
    }

    if (!blob || blob.size > maxSizeBytes) {
      return null;
    }

    return new File([blob], `${baseName}-compressed.${extension}`, {
      type: outputType,
      lastModified: Date.now(),
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log("[Profile] Selected profile image", {
      name: file.name,
      type: file.type,
      size: file.size,
    });

    if (!file.type.startsWith("image/")) {
      console.error("[Profile] Rejected image: invalid file type", {
        type: file.type,
      });
      showToast("Please select a valid image file (JPEG, PNG, GIF)", "error");
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    let fileToUse = file;

    if (file.size > maxSize) {
      console.warn("[Profile] Image too large, attempting compression", {
        originalSize: file.size,
        maxSize,
      });

      try {
        const compressedFile = await compressImageToLimit(file, maxSize);

        if (!compressedFile) {
          console.error("[Profile] Compression failed to meet 2MB limit", {
            originalSize: file.size,
          });
          showToast(
            "Image is too large and could not be compressed below 2MB. Please choose a smaller image.",
            "error"
          );
          return;
        }

        fileToUse = compressedFile;
        console.log("[Profile] Image compressed successfully", {
          originalSize: file.size,
          compressedSize: compressedFile.size,
          type: compressedFile.type,
        });
        showToast("Image optimized for upload", "success");
      } catch (compressionError) {
        console.error("[Profile] Compression error", compressionError);
        showToast("Unable to process image. Please choose another photo.", "error");
        return;
      }
    }

    setSelectedPhoto(fileToUse);

    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileImage(e.target.result);
      setImageLoaded(true);
    };
    reader.onerror = () => {
      console.error("[Profile] Failed to read selected image file");
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedPhoto(null);
    if (userData?.profile_photo) {
      setProfileImage(userData.profile_photo);
    } else if (userData?.files?.passport_photo) {
      setProfileImage(userData.files.passport_photo);
    } else {
      setProfileImage(DEFAULT_PROFILE_IMAGE);
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setProfileImage(DEFAULT_PROFILE_IMAGE);
    setImageLoaded(true);
  };

  const handleLogout = () => {
    setShowLogoutSuccess(true);

    setTimeout(() => {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("tokenExpires");
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("current_user_id");
      localStorage.removeItem("user_data");
      navigate("/login");
    }, 2000);
  };

  const closeLogoutModal = () => {
    setShowLogoutSuccess(false);
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    localStorage.removeItem("tokenExpires");
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("current_user_id");
    localStorage.removeItem("user_data");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="error-message">
          No user data found. Please <a href="/login">login again</a>.
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      {showLogoutSuccess && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <div className="logout-icon">👋</div>
            <h3>Logged Out Successfully</h3>
            <p>You have been securely logged out of your account.</p>
            <div className="logout-details">
              <div className="logout-item">
                <span className="logout-label">Session Ended:</span>
                <span className="logout-value">
                  {new Date().toLocaleTimeString()}
                </span>
              </div>
              <div className="logout-item">
                <span className="logout-label">Member ID:</span>
                <span className="logout-value">{userData.membership_id}</span>
              </div>
            </div>
            <div className="logout-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <span>Redirecting to login page...</span>
            </div>
            <button className="logout-close-btn" onClick={closeLogoutModal}>
              Go to Login Now
            </button>
          </div>
        </div>
      )}

      <div className="profile-header">
        <h2>Profile & ID Card</h2>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="membership-status card">
        <div className="status-left">
          <div className="profile-image-container">
            <img
              src={profileImage}
              alt="profile"
              className={`profile-img ${imageLoaded ? "loaded" : "loading"}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {isEditing && (
              <div className="profile-image-overlay">
                <label htmlFor="profile-upload" className="upload-btn">
                  📷 Change
                </label>
                {selectedPhoto && (
                  <button className="remove-btn" onClick={handleRemoveImage}>
                    🗑️ Remove
                  </button>
                )}
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
              </div>
            )}
          </div>
          <div>
            <h3 className="profile-name">
              {userData.first_name} {userData.surname}
              <span className="volunteer-badge">
                {userData.membership_category}
              </span>
            </h3>
            <p className="member-id-display">ID: {userData.membership_id}</p>
            <p className="member-status-badge">
              Status:{" "}
              <span
                className={`status ${userData.status?.toLowerCase() || "active"}`}
              >
                {userData.status || "Active"}
              </span>
            </p>
          </div>
        </div>
        <div className="status-right">
          <p>
            <strong>Email:</strong> {userData.email}
          </p>
          <p>
            <strong>Phone:</strong>{" "}
            {userData.telephone_numbers?.[0] ||
              userData.whatsapp_number ||
              "N/A"}
          </p>
          <p>
            <strong>WhatsApp:</strong> {userData.whatsapp_number || "N/A"}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <h4>Personal Details</h4>
        </div>
        <div className="form-grid">
          <div>
            <label>First Name</label>
            <input
              type="text"
              value={formData.first_name || ""}
              disabled={!isEditing}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
            />
          </div>
          <div>
            <label>Last Name</label>
            <input
              type="text"
              value={formData.surname || ""}
              disabled={!isEditing}
              onChange={(e) => handleInputChange("surname", e.target.value)}
            />
          </div>
          <div>
            <label>Email</label>
            <input
              type="email"
              value={formData.email || ""}
              disabled={!isEditing}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
          </div>
          <div>
            <label>Phone Number</label>
            <input
              type="text"
              value={formData.telephone_numbers?.[0] || ""}
              disabled={!isEditing}
              onChange={(e) => handlePhoneNumberChange(0, e.target.value)}
              placeholder="Enter your phone number"
            />
          </div>
          <div>
            <label>WhatsApp Number</label>
            <input
              type="text"
              value={formData.whatsapp_number || ""}
              disabled={!isEditing}
              onChange={(e) =>
                handleInputChange("whatsapp_number", e.target.value)
              }
              placeholder="Enter your WhatsApp number"
            />
          </div>
          <div>
            <label>Date of Birth</label>
            <input
              type="date"
              value={formData.date_of_birth || ""}
              disabled={!isEditing}
              onChange={(e) =>
                handleInputChange("date_of_birth", e.target.value)
              }
            />
          </div>
          <div>
            <label>Gender</label>
            <select
              value={formData.gender || ""}
              disabled={!isEditing}
              onChange={(e) => handleInputChange("gender", e.target.value)}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="full-width">
            <label>Residential Address</label>
            <textarea
              value={formData.full_house_address || ""}
              disabled={!isEditing}
              onChange={(e) =>
                handleInputChange("full_house_address", e.target.value)
              }
              rows="3"
              placeholder="Enter your complete residential address including street, city, and state"
            />
            <div className="field-hint">
              Please provide your complete residential address including street
              name, house number, city, and state
            </div>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="card">
          <div className="section-header">
            <h4>Change Password</h4>
            <p
              style={{
                color: "#666",
                fontSize: "14px",
                margin: "5px 0 0 0",
                fontStyle: "italic",
              }}
            >
              Leave blank if you don't want to change password
            </p>
          </div>
          {passwordError && <div className="error-banner">{passwordError}</div>}
          <div className="form-grid">
            <div>
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) =>
                  handlePasswordChange("current_password", e.target.value)
                }
                placeholder="Enter current password"
              />
            </div>
            <div>
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.password}
                onChange={(e) =>
                  handlePasswordChange("password", e.target.value)
                }
                placeholder="Enter new password"
              />
            </div>
            <div>
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.password_confirmation}
                onChange={(e) =>
                  handlePasswordChange(
                    "password_confirmation",
                    e.target.value
                  )
                }
                placeholder="Confirm new password"
              />
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <h4>Political Information</h4>
        <div className="political-info-grid">
          <div>
            <label>State</label>
            <input
              type="text"
              value={userData.geographic_info?.state || ""}
              disabled
            />
          </div>
          <div>
            <label>Senatorial District</label>
            <input
              type="text"
              value={userData.geographic_info?.sd || ""}
              disabled
            />
          </div>
          <div>
            <label>Federal Constituency</label>
            <input
              type="text"
              value={userData.geographic_info?.fc || ""}
              disabled
            />
          </div>
          <div>
            <label>Local Government</label>
            <input
              type="text"
              value={userData.geographic_info?.lg || ""}
              disabled
            />
          </div>
          <div>
            <label>Ward</label>
            <input
              type="text"
              value={userData.geographic_info?.ward || ""}
              disabled
            />
          </div>
          <div>
            <label>Polling Unit</label>
            <input
              type="text"
              value={userData.geographic_info?.pu || ""}
              disabled
            />
          </div>
        </div>
      </div>

      <div className="card membership-card">
        <div className="section-header">
          <h4>Membership Card</h4>
        </div>

        <PrintableIDCard
          ref={idCardRef}
          userData={userData}
          profileImage={profileImage}
        />

        <div className="card-actions">
          <button
            type="button"
            className="download-pdf-btn"
            onClick={handleDownloadIDCardPdf}
          >
            Download ID Card
          </button>

          <button
            className="edit-btn"
            onClick={handleEditToggle}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <div className="spinner-small"></div>
                Saving...
              </>
            ) : isEditing ? "Save Changes" : "Edit Profile"}
          </button>

          {isEditing && (
            <button
              className="cancel-btn"
              onClick={() => {
                setIsEditing(false);
                setFormData(
                  buildFormDataFromUser(userData, formData.full_house_address || "")
                );
                setPasswordData({
                  current_password: "",
                  password: "",
                  password_confirmation: "",
                });
                setSelectedPhoto(null);
                setPasswordError("");
                if (userData.profile_photo) {
                  setProfileImage(userData.profile_photo);
                } else if (userData.files?.passport_photo) {
                  setProfileImage(userData.files.passport_photo);
                }
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="logout-container">
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Profile;